import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import DashboardContent from "../components/DashboardContent";
import { CsrAPI } from "../apis/CsrAPI";
import { API_URL } from "../utils/DomainUtils";
import { ReactComponent as DeleteIcon } from "../assets/images/icons/delete-small.svg";

// ── Helpers ────────────────────────────────────────────────────────────────

function groupSessionsByDate(sessions) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const groups = { Today: [], Yesterday: [], "Last 7 days": [], Older: [] };
  for (const s of sessions) {
    const d = new Date(s.updated_at);
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (day >= today) groups["Today"].push(s);
    else if (day >= yesterday) groups["Yesterday"].push(s);
    else if (day >= sevenDaysAgo) groups["Last 7 days"].push(s);
    else groups["Older"].push(s);
  }
  return groups;
}

function toolLabel(name) {
  const labels = {
    list_studies: "Searching your studies",
    get_study_info: "Getting study details",
    list_documents: "Listing documents",
  };
  return labels[name] || `Calling ${name}`;
}

// ── TriloChat page ─────────────────────────────────────────────────────────

const TriloChat = () => {
  const { studyId: paramStudyId } = useParams();
  const { getAccessTokenSilently } = useAuth0();

  // Studies list for the selector
  const [studies, setStudies] = useState([]);
  // All sessions for the sidebar
  const [sessions, setSessions] = useState([]);
  // Currently open session
  const [currentSessionId, setCurrentSessionId] = useState(null);
  // Selected study for the current session
  const [selectedStudyId, setSelectedStudyId] = useState(paramStudyId || null);
  // Message history for the current session
  const [messages, setMessages] = useState([]);
  // Chat input
  const [input, setInput] = useState("");
  // Whether a response is being streamed
  const [streaming, setStreaming] = useState(false);
  // Which tools are currently being called (shown as indicators)
  const [activeTools, setActiveTools] = useState([]);
  // Partial assistant text while streaming (not yet committed to messages)
  const [streamingText, setStreamingText] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // ── Data loading ─────────────────────────────────────────────────────────

  const loadStudies = useCallback(async () => {
    try {
      const docs = await CsrAPI.getAllStudies();
      setStudies((docs || []).filter(d => d.study_type === "CSR"));
    } catch (e) {
      console.error("Failed to load studies", e);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const list = await CsrAPI.listChatSessions();
      setSessions(list || []);
    } catch (e) {
      console.error("Failed to load sessions", e);
    }
  }, []);

  const openSession = useCallback(async sessionId => {
    try {
      const session = await CsrAPI.getChatSession(sessionId);
      setCurrentSessionId(session.session_id);
      setSelectedStudyId(session.study_id || null);
      setMessages(session.messages || []);
      setActiveTools([]);
      setStreamingText(null);
    } catch (e) {
      console.error("Failed to open session", e);
    }
  }, []);

  useEffect(() => {
    loadStudies();
    loadSessions();
  }, [loadStudies, loadSessions]);

  // Scroll to bottom whenever messages or streaming text changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText, activeTools]);

  // ── Session management ───────────────────────────────────────────────────

  const handleNewSession = async () => {
    try {
      const session = await CsrAPI.createChatSession(selectedStudyId);
      setSessions(prev => [session, ...prev]);
      setCurrentSessionId(session.session_id);
      setMessages([]);
      setActiveTools([]);
      setStreamingText(null);
      inputRef.current?.focus();
    } catch (e) {
      console.error("Failed to create session", e);
    }
  };

  const handleSelectSession = async sessionId => {
    if (sessionId === currentSessionId) return;
    await openSession(sessionId);
  };

  const handleDeleteSession = async (e, sessionId) => {
    e.stopPropagation();
    try {
      await CsrAPI.deleteChatSession(sessionId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
        setActiveTools([]);
        setStreamingText(null);
      }
    } catch (e) {
      console.error("Failed to delete session", e);
    }
  };

  const handleStudyChange = async newStudyId => {
    setSelectedStudyId(newStudyId || null);
    if (currentSessionId) {
      try {
        await CsrAPI.updateChatSession(currentSessionId, newStudyId || null);
      } catch (e) {
        console.error("Failed to update session study", e);
      }
    }
  };

  // ── Message sending ──────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    let sessionId = currentSessionId;

    // Auto-create session if none is open
    if (!sessionId) {
      try {
        const session = await CsrAPI.createChatSession(selectedStudyId);
        setSessions(prev => [session, ...prev]);
        setCurrentSessionId(session.session_id);
        sessionId = session.session_id;
      } catch (e) {
        console.error("Failed to create session", e);
        return;
      }
    }

    setInput("");
    setStreaming(true);
    setActiveTools([]);
    setStreamingText(null);

    // Optimistically add user message
    setMessages(prev => [...prev, { role: "user", content: text }]);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_URL}/chat/sessions/${sessionId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message: text }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      let done = false;
      let value;
      while (!done) {
        ({ done, value } = await reader.read());
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6).trim();
          if (!raw) continue;

          let event;
          try {
            event = JSON.parse(raw);
          } catch {
            continue;
          }

          if (event.type === "tool_call") {
            setActiveTools(prev => [...prev, event.name]);
          } else if (event.type === "text") {
            setActiveTools([]);
            setStreamingText(null);
            setMessages(prev => [
              ...prev,
              { role: "assistant", content: event.content },
            ]);
            // Update session title in sidebar if it was "New chat"
            setSessions(prev =>
              prev.map(s =>
                s.session_id === sessionId
                  ? {
                      ...s,
                      title:
                        s.title === "New chat" ? text.slice(0, 60) : s.title,
                      updated_at: new Date().toISOString(),
                    }
                  : s
              )
            );
          } else if (event.type === "done") {
            setStreaming(false);
          } else if (event.type === "error") {
            setActiveTools([]);
            setStreamingText(null);
            setMessages(prev => [
              ...prev,
              {
                role: "assistant",
                content:
                  event.message || "An error occurred. Please try again.",
              },
            ]);
            setStreaming(false);
          }
        }
      }
    } catch (e) {
      console.error("Stream failed", e);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "Connection error. Please try again." },
      ]);
    } finally {
      setStreaming(false);
      setActiveTools([]);
      setStreamingText(null);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const sessionGroups = groupSessionsByDate(sessions);
  const currentStudy = studies.find(s => s.id === selectedStudyId);

  const pageContent = (
    <div className="trilochat">
      {/* Session sidebar */}
      <div className="trilochat__sessions">
        <div className="trilochat__sessions-header">
          <h3>TriloChat</h3>
          <button
            className="trilochat__sessions-new"
            onClick={handleNewSession}>
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z" />
            </svg>
            New chat
          </button>
        </div>

        <div className="trilochat__sessions-list">
          {sessions.length === 0 && (
            <div
              style={{
                padding: "16px",
                fontSize: "13px",
                color: "#777e7e",
                textAlign: "center",
              }}>
              No conversations yet
            </div>
          )}
          {Object.entries(sessionGroups).map(([label, group]) =>
            group.length === 0 ? null : (
              <div key={label} className="trilochat__sessions-group">
                <div className="trilochat__sessions-group-label">{label}</div>
                {group.map(s => (
                  <div
                    key={s.session_id}
                    className={`trilochat__sessions-item ${s.session_id === currentSessionId ? "trilochat__sessions-item--active" : ""}`}
                    onClick={() => handleSelectSession(s.session_id)}>
                    <span className="trilochat__sessions-item-title">
                      {s.title || "New chat"}
                    </span>
                    <button
                      className="trilochat__sessions-item-delete"
                      title="Delete conversation"
                      onClick={e => handleDeleteSession(e, s.session_id)}>
                      <DeleteIcon />
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>

      {/* Chat panel */}
      <div className="trilochat__panel">
        {/* Header with study selector */}
        <div className="trilochat__panel-header">
          <span className="trilochat__study-label">Study:</span>
          <select
            className="trilochat__study-select"
            value={selectedStudyId || ""}
            onChange={e => handleStudyChange(e.target.value || null)}>
            <option value="">— General (no study) —</option>
            {studies.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Messages */}
        <div className="trilochat__messages">
          {messages.length === 0 && !streaming && (
            <div className="trilochat__empty">
              <div className="trilochat__empty-icon">💬</div>
              <h3 className="trilochat__empty-title">
                {currentStudy
                  ? `Ask about ${currentStudy.name}`
                  : "Ask anything about your studies"}
              </h3>
              <p className="trilochat__empty-hint">
                {currentStudy
                  ? "Ask about sections, documents, protocol deviations, demographics, and more."
                  : "Select a study above or ask a general question. TriloChat will look up what it needs."}
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`trilochat__message trilochat__message--${msg.role}`}>
              <div className="trilochat__message-sender">
                {msg.role === "user" ? "You" : "TriloChat"}
              </div>
              <div className="trilochat__message-bubble">{msg.content}</div>
            </div>
          ))}

          {/* Tool call indicators while waiting */}
          {streaming && activeTools.length > 0 && (
            <div className="trilochat__message trilochat__message--assistant">
              <div className="trilochat__message-sender">TriloChat</div>
              {activeTools.slice(-1).map((tool, i) => (
                <div key={i} className="trilochat__tool-indicator">
                  <div className="trilochat__tool-indicator-dot" />
                  {toolLabel(tool)}…
                </div>
              ))}
            </div>
          )}

          {/* Thinking indicator when streaming but no tool yet */}
          {streaming && activeTools.length === 0 && (
            <div className="trilochat__message trilochat__message--assistant">
              <div className="trilochat__message-sender">TriloChat</div>
              <div className="trilochat__tool-indicator">
                <div className="trilochat__tool-indicator-dot" />
                Thinking…
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="trilochat__input-area">
          <textarea
            ref={inputRef}
            className="trilochat__input"
            placeholder={
              streaming
                ? "TriloChat is responding…"
                : currentStudy
                  ? `Ask about ${currentStudy.name}…`
                  : "Ask anything about your studies…"
            }
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={streaming}
            rows={1}
          />
          <button
            className="trilochat__send"
            onClick={handleSend}
            disabled={streaming || !input.trim()}>
            {streaming ? "…" : "Send"}
            {!streaming && (
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="currentColor">
                <path d="M2.5 13.5 14 8 2.5 2.5v4l8.5 1.5-8.5 1.5v4Z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return <DashboardContent pageContent={pageContent} />;
};

export default TriloChat;
